import { useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useBackendHealth } from './useBackendHealth';
import { socketBaseUrl, socketIoOptions } from '../utils/socketBaseUrl';

type FoodOrderAlertHandler = (payload: {
  type?: string;
  status?: string;
  orderNumber?: string;
  orderId?: string;
}) => void;

/** Subscribe to food order realtime updates for vendor/admin live panels. */
export function useFoodOrderAlerts(onEvent?: FoodOrderAlertHandler) {
  const socketRef = useRef<Socket | null>(null);
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;
  const lastAlertRef = useRef<string>('');
  const { isBackendUp } = useBackendHealth();

  const playAlertSound = useCallback(() => {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const beep = (t: number, f: number) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
        o.connect(g);
        g.connect(ctx.destination);
        o.start(t);
        o.stop(t + 0.25);
      };
      beep(now, 880);
      beep(now + 0.28, 1175);
      beep(now + 0.56, 880);
      window.setTimeout(() => ctx.close(), 1200);
    } catch {
      // ignore audio errors (autoplay policies)
    }
  }, []);

  const connect = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (!token || !isBackendUp) return;
    if (socketRef.current?.connected) return;

    const base = socketBaseUrl();
    const socket = io(base, {
      auth: { token },
      ...socketIoOptions(),
    });

    socket.on('connect', () => {
      // Auth handshake joins tenant:{id} automatically on the server.
    });
    socket.io.on('reconnect', () => {
      // rejoin happens in socket auth middleware
    });

    const onUpdate = (data: Record<string, unknown>) => {
      const type = String(data?.type ?? '');
      const status = String(data?.status ?? '');
      const orderId = String(data?.orderId ?? data?._id ?? '');
      const orderNumber = String(data?.orderNumber ?? '');
      const key = `${type}:${orderId}:${status}`;
      if (type === 'food_order_placed' || (status === 'pending' && type.includes('food'))) {
        if (lastAlertRef.current !== key) {
          lastAlertRef.current = key;
          playAlertSound();
        }
      }
      handlerRef.current?.({ type, status, orderId, orderNumber });
    };

    socket.on('orders:update', onUpdate);
    socket.on('orders/update', onUpdate);

    socketRef.current = socket;
  }, [isBackendUp, playAlertSound]);

  useEffect(() => {
    if (!isBackendUp) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }
    connect();
    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [connect, isBackendUp]);

  return { reconnect: connect, playAlertSound };
}
